import { useEffect, useState } from 'react';
import { Link, useLocation } from 'wouter';
import { compradorTrpc } from '../../lib/compradorTrpc';
import { AppShellComprador } from '../../components/comprador/AppShellComprador';
import { brl, dataCurta, mesCurto, useCompradorSessao, sairParaLogin, rotaApp } from './util';

interface Painel {
  primeiroNome: string;
  unidade: string;
  valorContrato: number;
  valorPago: number;
  saldoDevedor: number;
  percentualQuitado: number;
  proximaParcela: { valor: number; vencimento: string | null } | null;
  reajuste: { dias: number; data: string; parcelaAtual: number; parcelaNova: number | null } | null;
  obra: { fase: string | null; entregaPrevista: string | null; percentual: number | null } | null;
}

interface Me {
  orgNome: string;
  orgLogoUrl: string | null;
  corMarca: string | null;
  dadosAtualizadosEm: string | Date | null;
}

/**
 * Início — "como estou?" (design aprovado). Tom de conquista: "Você já
 * conquistou R$ X", nunca linguagem de dívida/cobrança.
 */
export function InicioComprador() {
  const slug = useCompradorSessao();
  const [, navigate] = useLocation();
  const [me, setMe] = useState<Me | null>(null);
  const [painel, setPainel] = useState<Painel | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [semContrato, setSemContrato] = useState(false);

  useEffect(() => {
    if (!slug) return;
    let vivo = true;
    Promise.all([
      compradorTrpc.auth.me.query({ orgSlug: slug }),
      compradorTrpc.app.painel.query({ orgSlug: slug }),
    ])
      .then(([m, p]) => {
        if (!vivo) return;
        setMe(m);
        setPainel(p);
      })
      .catch((e: Error) => {
        if (!vivo) return;
        if (/contrato ativo/i.test(e.message)) setSemContrato(true);
        else sairParaLogin(slug, navigate);
      })
      .finally(() => vivo && setCarregando(false));
    return () => {
      vivo = false;
    };
  }, [slug, navigate]);

  if (carregando) {
    return (
      <AppShellComprador corMarca={me?.corMarca} orgNome={me?.orgNome}>
        <p className="py-20 text-center text-sm text-ink-3">Carregando…</p>
      </AppShellComprador>
    );
  }

  if (semContrato || !painel) {
    return (
      <AppShellComprador corMarca={me?.corMarca} orgNome={me?.orgNome}>
        <div className="py-20 text-center" data-testid="inicio-sem-contrato">
          <h1 className="font-serif text-xl text-ink">Seu contrato está a caminho</h1>
          <p className="mx-auto mt-2 max-w-sm text-sm text-ink-3">
            Assim que a {me?.orgNome ?? 'incorporadora'} confirmar seu cadastro, você acompanha tudo por aqui.
          </p>
        </div>
      </AppShellComprador>
    );
  }

  const faltam = Math.max(0, painel.valorContrato - painel.valorPago);
  const pct = Math.min(100, Math.max(0, painel.percentualQuitado));

  return (
    <AppShellComprador corMarca={me?.corMarca} orgNome={me?.orgNome}>
      <div data-testid="inicio-comprador">
        {/* Saudação */}
        <div className="mb-5 flex items-start justify-between">
          <div>
            <div className="text-sm text-ink-3">Olá, {painel.primeiroNome}</div>
            <h1 className="mt-0.5 font-serif text-[22px] font-medium tracking-tight text-ink">
              {painel.unidade} · {me?.orgNome}
            </h1>
          </div>
          {me?.orgLogoUrl ? (
            // rounded-xl (não rounded-full): a logo aparece COMPLETA, sem ser
            // recortada num círculo/formato diferente do da imagem.
            <img src={me.orgLogoUrl} alt={me.orgNome} className="h-11 w-11 shrink-0 rounded-xl object-contain" />
          ) : (
            <div
              className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-sm font-semibold text-white"
              style={{ background: 'var(--brand)' }}
              aria-hidden="true"
            >
              {painel.primeiroNome.slice(0, 2).toUpperCase()}
            </div>
          )}
        </div>

        {/* Hero: você já conquistou */}
        <section
          className="relative overflow-hidden rounded-3xl p-6 text-white"
          style={{ background: 'linear-gradient(135deg, var(--brand), var(--brand-deep))' }}
          data-testid="inicio-hero"
        >
          <div
            className="pointer-events-none absolute -right-10 -top-16 h-44 w-44 rounded-full"
            style={{ background: 'rgba(255,255,255,.08)' }}
            aria-hidden="true"
          />
          <div className="text-[13px] text-white/85">Você já conquistou</div>
          <div className="num mt-1 font-serif text-[38px] font-medium leading-none tracking-tight">
            {brl(painel.valorPago)}
          </div>
          <div className="mt-1.5 text-[13px] text-white/85">
            de {brl(painel.valorContrato)} do seu imóvel
          </div>
          <div className="mt-4 h-1.5 overflow-hidden rounded-full bg-white/25">
            <div className="h-full rounded-full bg-white" style={{ width: `${pct}%` }} />
          </div>
          <div className="mt-2 flex justify-between text-[12.5px] text-white/90">
            <span>{pct}% quitado</span>
            <span>faltam {brl(faltam)}</span>
          </div>
        </section>

        {/* Próxima parcela + saldo */}
        <div className="mt-3 grid grid-cols-2 gap-3">
          <div className="card p-4" data-testid="inicio-proxima-parcela">
            <div className="text-xs text-ink-3">Próxima parcela</div>
            <div className="num mt-1.5 font-serif text-[22px] font-medium text-ink">
              {painel.proximaParcela ? brl(painel.proximaParcela.valor) : 'Nenhuma'}
            </div>
            <div className="mt-0.5 text-xs text-ink-3">
              {painel.proximaParcela?.vencimento
                ? `vence ${dataCurta(painel.proximaParcela.vencimento)}`
                : 'tudo em dia'}
            </div>
          </div>
          <div className="card p-4">
            <div className="text-xs text-ink-3">Saldo devedor</div>
            <div className="num mt-1.5 font-serif text-[22px] font-medium text-ink">{brl(painel.saldoDevedor)}</div>
            <div className="mt-0.5 text-xs text-ink-3">atualizado hoje</div>
          </div>
        </div>

        {/* Aviso de reajuste chegando — informativo e calmo, nunca ameaçador */}
        {painel.reajuste && (
          <section
            className="mt-3 rounded-2xl border p-4"
            style={{ background: 'var(--amber-soft)', borderColor: 'var(--amber-line)' }}
            data-testid="inicio-reajuste"
          >
            <div className="flex items-center gap-2 text-[13.5px] font-semibold" style={{ color: 'var(--amber-ink)' }}>
              <svg viewBox="0 0 24 24" width="16" height="16" fill="none" aria-hidden="true">
                <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.6" />
                <path d="M12 7.5V12l3 2" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
              </svg>
              Seu reajuste anual chega em {painel.reajuste.dias} {painel.reajuste.dias === 1 ? 'dia' : 'dias'}
            </div>
            <p className="mt-1.5 text-[13px] leading-relaxed text-ink-2">
              É o reajuste normal do contrato pelo INCC.{' '}
              {painel.reajuste.parcelaNova !== null ? (
                <>
                  Sua parcela vai passar de <strong>{brl(painel.reajuste.parcelaAtual)}</strong> para{' '}
                  <strong>{brl(painel.reajuste.parcelaNova)}</strong>. Já deixamos tudo explicado para você.
                </>
              ) : (
                <>O valor exato vai aparecer aqui assim que for confirmado. Já deixamos tudo explicado para você.</>
              )}
            </p>
            <Link
              href={rotaApp(slug!, '/conversa')}
              className="mt-3 inline-block rounded-full border px-4 py-2 text-[13px] font-semibold"
              style={{ borderColor: 'var(--amber-line)', color: 'var(--amber-ink)', background: 'white' }}
            >
              Ver como fica minha parcela
            </Link>
          </section>
        )}

        {/* Resumo da obra */}
        {painel.obra && (
          <section className="card mt-3 p-4" data-testid="inicio-obra">
            <div className="flex items-center justify-between">
              <div className="text-[15px] font-semibold text-ink">Sua obra</div>
              <Link href={rotaApp(slug!, '/obra')} className="text-[13px] font-semibold" style={{ color: 'var(--brand)' }}>
                Ver cronograma
              </Link>
            </div>
            <div className="mt-3 flex items-center gap-3">
              <span
                className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl"
                style={{ background: 'var(--brand-soft)', color: 'var(--brand)' }}
                aria-hidden="true"
              >
                <svg viewBox="0 0 24 24" width="20" height="20" fill="none">
                  <path d="M5 20V9.5L12 4l7 5.5V20" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
                  <path d="M9 20v-5h6v5" stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round" />
                </svg>
              </span>
              <div className="min-w-0">
                <div className="text-sm font-medium text-ink">
                  {painel.obra.fase ? `Fase de ${painel.obra.fase.toLowerCase()}` : 'Cronograma em preparação'}
                </div>
                <div className="text-xs text-ink-3">
                  {painel.obra.entregaPrevista ? `Entrega prevista: ${mesCurto(painel.obra.entregaPrevista)}` : 'Entrega a confirmar'}
                </div>
              </div>
              {painel.obra.percentual !== null && (
                <div className="ml-auto text-[13px] font-semibold" style={{ color: 'var(--brand)' }}>
                  {painel.obra.percentual}% concluída
                </div>
              )}
            </div>
          </section>
        )}

        {/* Data-base dos números (transparência de frescor da carteira) */}
        {me?.dadosAtualizadosEm && (
          <p className="mt-5 text-center text-xs text-ink-4" data-testid="comprador-data-base">
            Com base nos dados enviados pela {me.orgNome} em{' '}
            {new Date(me.dadosAtualizadosEm).toLocaleDateString('pt-BR')}.
          </p>
        )}
      </div>
    </AppShellComprador>
  );
}
